import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const error = searchParams.get('error');
        const message = searchParams.get('message');

        if (error || message) {
          toast.error(message || 'Anmeldung fehlgeschlagen');
          navigate('/login');
          return;
        }

        if (!token || provider !== 'azure') {
          toast.error('Ungültiger Callback');
          navigate('/login');
          return;
        }

        // Token im Auth Store speichern
        apiService.setToken(token);

        // Benutzerprofil abrufen
        const userResponse = await apiService.get('/auth/profile');
        
        // Benutzer anmelden
        login(userResponse, token);

        toast.success('Anmeldung erfolgreich!');

        let destination = '/dashboard';
        try {
          const setup = await apiService.get<{ needsSetup?: boolean; hasCompanyName?: boolean }>(
            '/system/public/setup-status'
          );
          const incomplete =
            setup?.hasCompanyName === false ||
            (setup?.hasCompanyName !== true && setup?.needsSetup === true);
          if (incomplete && userResponse?.role === 'SUPERADMIN') {
            destination = '/setup';
          }
        } catch (setupError) {
          console.error('Could not check first-run setup:', setupError);
        }

        navigate(destination);
      } catch (error: any) {
        console.error('Auth callback error:', error);
        toast.error('Fehler bei der Anmeldung');
        navigate('/login');
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <h2 className="text-2xl font-bold">Anmeldung läuft...</h2>
          <p className="text-muted-foreground">
            {isProcessing 
              ? 'Verarbeite Azure AD Anmeldung...' 
              : 'Weiterleitung...'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
